import type { SvgBondStereoLabel } from './usePaperGeometry';
import { nodeNumberBadgeBox } from '../chemistry/chargePlacement';
import type { CalloutTarget } from '../domain/calloutTargets';
import { type CSSProperties } from 'react';
import { PaperShapeAnnotation } from './PaperShapeAnnotation';
import { isBackgroundShape } from '../domain/annotationRendering';
import { atomLabelColor, type AtomAppearance, type BondAppearance } from '../domain/moleculeAppearance';
import type { MarkushAttachmentLine } from '../domain/markushLayers';
import { pointsToUnits } from '../domain/page';
import type { RepeatUnitBrackets } from '../domain/markushRepeatBrackets';
import { PaperBondGeometry } from './PaperBondGeometry';
import type { ObjectHandle } from '../domain/objectEditing';
import { arrowHandlePoints } from '../domain/arrowEditing';
import type { DrawingAtom } from '@peruselab/structure-editor/domain/graphDrawing';
import type { DrawingLine, NormalizedPoint } from '../model';
import { hasSelection, type EditorSelection } from '@peruselab/structure-editor/domain/editor';
import { normalizedToPixel, type PixelPoint } from '@peruselab/structure-editor/domain/bonds';
import { roundToOneDecimal, type PaperSize } from '@peruselab/structure-editor/domain/geometry';
import type { ViewportTransform } from '@peruselab/structure-editor/domain/viewport';
import { implicitHydrogenLabel } from '@peruselab/structure-editor/chemistry/elements';
import { atomShowsLabel } from '@peruselab/structure-editor/chemistry/labelMetrics';
import { ArrowheadDefs } from './ArrowheadDefs';
import { PaperTextAnnotation } from './PaperTextAnnotation';
import { DEFAULT_ARROW_STROKE_WIDTH } from '../rendering/arrowHeadGeometry';
import { reactionArrowPrimitives } from '@peruselab/structure-editor/rendering/reactionArrowGeometry';
import {
  ANNOTATION_DASH,
  atomEndGaps,
  curvedArrowPrimitives
} from '@peruselab/structure-editor/rendering/annotationGeometry';
import type { RenderableAnnotation } from '@peruselab/structure-editor/domain/annotationRendering';
import type {
  BlinkMarker,
  ProposalMarker,
  RenderedSvgAtom,
  SvgDraftLine,
  SvgHaptoLabel,
  SvgIonicLink,
  SvgLineSegment,
  SvgStereoWedge
} from './drawingPaperRenderModel';

const pointsAttr = (points: readonly { readonly x: number; readonly y: number }[]): string =>
  points.map((point) => `${point.x},${point.y}`).join(' ');

/** ADR 0014 -- a reaction zone to draw around the arrow (paper pixels). */
export interface PaperReactionZone {
  readonly zone: 'reactants' | 'products' | 'above' | 'below';
  readonly rect: { readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number };
  readonly empty: boolean;
  readonly label: string;
  /** The molecules in the zone -- dragging the zone moves them together. */
  readonly atomIds: readonly string[];
  /** The same atoms grouped per molecule (for whole-molecule selection inside a box). */
  readonly molecules: readonly (readonly string[])[];
  /** #739 -- where the "+" signs between this box's molecules go (reactants and products only). */
  readonly plusSigns?: readonly { readonly x: number; readonly y: number }[];
  /** #1137 -- a mark under each reagent typed in this box that was recognised (found: looked up). */
  readonly textMarks?: readonly { readonly x: number; readonly y: number; readonly found: boolean }[];
  /** #700 -- while something is dragged: it can go in this box, or it is over it. */
  readonly dropState?: 'candidate' | 'over' | undefined;
}

/** #700 -- each box's element, for the app's drag-and-drop (a box is a drop target). */
export type ReactionZoneRefs = Readonly<Partial<Record<PaperReactionZone['zone'], (element: Element | null) => void>>>;

interface PaperVectorLayerProps {
  readonly cycleFills?: readonly import('../domain/cycleFills').CycleFill[] | undefined;
  // ADR 0014 P1 -- the four zones around a reaction arrow, drawn under everything else.
  readonly reactionZones?: readonly PaperReactionZone[];
  readonly reactionZoneRefs?: ReactionZoneRefs | undefined;
  // #650 -- Markush attachment-point lines (label -> ring centre), paper-normalized. Decoration
  // derived from the Markush layers, not graph bonds, so they sit outside .committed-lines.
  readonly markushAttachmentLines?: readonly MarkushAttachmentLine[];
  // #656 -- Markush repeat-unit brackets and count labels, in paper pixels.
  readonly repeatUnitBrackets?: readonly RepeatUnitBrackets[];
  readonly paperSize: PaperSize;
  readonly viewport: ViewportTransform;
  readonly labelScale: number;
  readonly bondStrokeWidth: number;
  /** #868 -- per-atom / per-bond appearance from the depiction (molecule styling). */
  readonly atomAppearance?: Readonly<Record<string, AtomAppearance>> | undefined;
  readonly bondAppearance?: Readonly<Record<string, BondAppearance>> | undefined;
  readonly svgLines: readonly SvgLineSegment[];
  // ADR 0022 (as amended) -- the bolts between a salt's ions, and which of them show.
  readonly ionicLinks?: readonly SvgIonicLink[];
  readonly shownIonicIds?: ReadonlySet<string>;
  readonly haptoLabels?: readonly SvgHaptoLabel[];
  readonly stereoWedges: readonly SvgStereoWedge[];
  readonly ringPreviewLines: readonly { readonly id: string; readonly start: PixelPoint; readonly end: PixelPoint }[];
  readonly chainPreviewLines: readonly { readonly id: string; readonly start: PixelPoint; readonly end: PixelPoint }[];
  readonly selection: EditorSelection;
  readonly renderedLines: readonly DrawingLine[];
  readonly renderedAtoms: readonly DrawingAtom[];
  readonly svgAtoms: readonly RenderedSvgAtom[];
  /** #780 -- E/Z beside each defined double bond, drawn with the R/S labels. */
  readonly bondStereoLabels?: readonly SvgBondStereoLabel[];
  readonly svgDrafts: readonly SvgDraftLine[];
  readonly proposalMarkers: readonly ProposalMarker[];
  readonly hoveredEndpoint: PixelPoint | null;
  readonly offeredExistingEndpoints: readonly (PixelPoint & { readonly pointerId: number })[];
  readonly blinkMarkers: readonly BlinkMarker[];
  readonly invalidAtomIds: ReadonlySet<string>;
  readonly invalidBondIds: ReadonlySet<string>;
  // ADR 0020 D3 (#1099) -- the server check's warnings, marked amber.
  readonly warningAtomIds?: ReadonlySet<string>;
  readonly warningBondIds?: ReadonlySet<string>;
  readonly proposalRadiusPx: number;
  readonly annotations: readonly RenderableAnnotation[];
  /** #933: atoms and bonds an arrow being drawn or edited snaps to -- ringed so you see the target. */
  readonly arrowSnapTargets?: readonly NormalizedPoint[];
  readonly calloutTargets?: readonly CalloutTarget[];
  readonly activeCalloutTarget?: string;
  readonly draftGeometry?: {
    svgLines: readonly SvgLineSegment[];
    stereoWedges: readonly SvgStereoWedge[];
    bondStrokeWidth: number;
  };
  readonly ringGeometry?: {
    svgLines: readonly SvgLineSegment[];
    stereoWedges: readonly SvgStereoWedge[];
    bondStrokeWidth: number;
  };
  readonly chainGeometry?: {
    svgLines: readonly SvgLineSegment[];
    stereoWedges: readonly SvgStereoWedge[];
    bondStrokeWidth: number;
  };
  readonly objectHandles?: readonly ObjectHandle[];
  readonly annotationStrokeWidth?: number;
  // #575 -- a debug/reference overlay labeling every atom with its graph node id, independent of
  // atomShowsLabel (which only shows a *chemistry* symbol for atoms that need one, e.g. not a
  // bare skeletal-formula carbon vertex). Off by default.
  readonly showNodeNumbers?: boolean;
  readonly showStereoLabels?: boolean;
  // #576 -- highlights every atom with open valence (hasOpenValence -- the atom's bond-order sum
  // is below its maximum supported valence). Off by default.
  readonly showBondAcceptors?: boolean;
  // #577 -- the custom rotation pivot (Center of Rotation toggle), already resolved to a pixel
  // point (custom placement, or the selection's centroid when none is set) by DrawingPaper. Null
  // when the toggle is off or the selection doesn't qualify (fewer than 2 atoms).
  readonly rotationPivotMarker?: PixelPoint | null;
  // #578 -- live drag previews for the rectangular marquee and the freehand lasso selection,
  // already in pixel space. Null when that particular drag isn't in progress.
  readonly marqueePreview?: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  } | null;
  readonly lassoPreview?: readonly PixelPoint[] | null;
  // Live bounding box while a decorative shape is being placed, in pixel space.
  readonly shapePreview: {
    readonly minX: number;
    readonly minY: number;
    readonly maxX: number;
    readonly maxY: number;
  } | null;
}

const NO_IDS: ReadonlySet<string> = new Set();

export function PaperVectorLayer({
  paperSize,
  viewport,
  labelScale,
  bondStrokeWidth,
  atomAppearance,
  bondAppearance,
  cycleFills,
  svgLines,
  ionicLinks,
  shownIonicIds,
  haptoLabels,
  stereoWedges,
  ringPreviewLines,
  chainPreviewLines,
  selection,
  renderedLines,
  renderedAtoms,
  svgAtoms,
  svgDrafts,
  proposalMarkers,
  hoveredEndpoint,
  offeredExistingEndpoints,
  blinkMarkers,
  invalidAtomIds,
  invalidBondIds,
  warningAtomIds = NO_IDS,
  warningBondIds = NO_IDS,
  proposalRadiusPx,
  annotations,
  arrowSnapTargets = [],
  calloutTargets = [],
  activeCalloutTarget,
  objectHandles = [],
  draftGeometry,
  ringGeometry,
  chainGeometry,
  annotationStrokeWidth = DEFAULT_ARROW_STROKE_WIDTH,
  shapePreview,
  showNodeNumbers = false,
  showStereoLabels = false,
  bondStereoLabels = [],
  showBondAcceptors = false,
  rotationPivotMarker = null,
  marqueePreview = null,
  lassoPreview = null,
  markushAttachmentLines = [],
  repeatUnitBrackets = [],
  reactionZones = [],
  reactionZoneRefs
}: PaperVectorLayerProps) {
  return (
    <svg
      className="paper-layer vector-layer"
      width="100%"
      height="100%"
      viewBox={`${-viewport.panX / viewport.zoom} ${-viewport.panY / viewport.zoom} ${paperSize.width / viewport.zoom} ${paperSize.height / viewport.zoom}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <ArrowheadDefs scale={labelScale} />
      <g className="reaction-zones">
        {reactionZones.map(({ zone, rect, empty, label, dropState }) => (
          <g
            key={zone}
            className={`reaction-zone reaction-zone--${zone}${empty ? ' reaction-zone--empty' : ''}${dropState ? ` reaction-zone--drop-${dropState}` : ''}`}
          >
            <rect
              ref={reactionZoneRefs?.[zone]}
              data-reaction-zone={zone}
              x={rect.minX}
              y={rect.minY}
              width={rect.maxX - rect.minX}
              height={rect.maxY - rect.minY}
              rx={10 / viewport.zoom}
            />
            {/* Just outside the zone's top edge, so it never sits on the structure inside -- the Solvents
                box's under its bottom edge, clear of what settles under the arrow (#1137 follow-up). */}
            <text
              x={rect.minX + 4 / viewport.zoom}
              y={zone === 'below' ? rect.maxY + 14 / viewport.zoom : rect.minY - 5 / viewport.zoom}
              fontSize={11 / viewport.zoom}
            >
              {label}
            </text>
          </g>
        ))}
      </g>
      {/* #739 -- "+" between the reactants and between the products. Part of the depiction, so it
          scales with the drawing like an atom label (labelScale included), drawn under the bonds. */}
      {/* #1137 -- under a reagent typed by the arrow: a tick once it is recognised, a dot while it
          is looked up or not found (the Conditions card says which). */}
      <g className="reaction-text-marks" data-testid="reaction-text-marks">
        {reactionZones.flatMap(({ zone, textMarks = [] }) =>
          textMarks.map((mark, index) =>
            mark.found ? (
              <path
                key={`${zone}-${index}`}
                className="reaction-text-mark reaction-text-mark--found"
                data-reaction-zone={zone}
                d={`M ${mark.x - 4} ${mark.y} l 3 3 l 6 -6`}
              />
            ) : (
              <circle
                key={`${zone}-${index}`}
                className="reaction-text-mark"
                data-reaction-zone={zone}
                cx={mark.x}
                cy={mark.y}
                r={2}
              />
            )
          )
        )}
      </g>
      <g className="reaction-plus-signs" data-testid="reaction-plus-signs">
        {reactionZones.flatMap(({ zone, plusSigns = [] }) =>
          plusSigns.map((plus, index) => (
            <text
              key={`${zone}-${index}`}
              className="reaction-plus"
              data-reaction-zone={zone}
              x={plus.x}
              y={plus.y}
              transform={
                labelScale === 1
                  ? undefined
                  : `translate(${plus.x} ${plus.y}) scale(${labelScale}) translate(${-plus.x} ${-plus.y})`
              }
            >
              +
            </text>
          ))
        )}
      </g>
      <g className="cycle-fills" pointerEvents="none">
        {(cycleFills ?? []).map((fill) => {
          const points = fill.atomIds.map((id) => renderedAtoms.find((atom) => atom.id === id)?.position);
          if (points.some((point) => !point)) return null;
          return (
            <polygon
              key={JSON.stringify(fill.atomIds)}
              data-cycle-atoms={fill.atomIds.join(' ')}
              points={points.map((point) => `${point!.x * paperSize.width},${point!.y * paperSize.height}`).join(' ')}
              fill={fill.color}
              stroke="none"
            />
          );
        })}
      </g>
      <g className="committed-lines">
        <PaperBondGeometry
          svgLines={svgLines}
          stereoWedges={stereoWedges}
          ionicLinks={ionicLinks}
          shownIonicIds={shownIonicIds}
          haptoLabels={haptoLabels}
          labelFontSize={13 * labelScale}
          bondStrokeWidth={bondStrokeWidth}
          viewportZoom={viewport.zoom}
          bondLook={
            bondAppearance
              ? (bondId) => {
                  const look = bondAppearance[bondId];
                  if (!look) return undefined;
                  return {
                    ...(look.color ? { color: look.color } : {}),
                    // #868 -- a width in points, drawn at its real size (as annotation widths, #859).
                    ...(look.lineWidth !== undefined
                      ? { strokeWidth: pointsToUnits(look.lineWidth) * viewport.zoom }
                      : {})
                  };
                }
              : undefined
          }
        />
      </g>
      <g className="markush-attachment-lines" style={{ strokeWidth: bondStrokeWidth }}>
        {markushAttachmentLines.map((line) => (
          <line
            key={line.id}
            x1={line.start.x * paperSize.width}
            y1={line.start.y * paperSize.height}
            x2={line.end.x * paperSize.width}
            y2={line.end.y * paperSize.height}
          />
        ))}
      </g>
      <g className="markush-repeat-brackets" style={{ strokeWidth: bondStrokeWidth * 0.8 }}>
        {repeatUnitBrackets.map((unit) => (
          <g key={unit.id}>
            {unit.strokes.map((stroke, index) => (
              <polyline key={index} points={stroke.map((point) => `${point.x},${point.y}`).join(' ')} />
            ))}
            <text className="markush-repeat-label" x={unit.label.x} y={unit.label.y} data-repeat-unit-label={unit.id}>
              {unit.label.text}
            </text>
          </g>
        ))}
      </g>
      <g className={`ring-template-preview ${ringGeometry ? 'active-geometry-preview' : ''}`}>
        {ringGeometry ? (
          <PaperBondGeometry {...ringGeometry} viewportZoom={viewport.zoom} />
        ) : (
          ringPreviewLines.map((preview) => (
            <line key={preview.id} x1={preview.start.x} y1={preview.start.y} x2={preview.end.x} y2={preview.end.y} />
          ))
        )}
      </g>
      <g className={`chain-template-preview ${chainGeometry ? 'active-geometry-preview' : ''}`}>
        {chainGeometry ? (
          <PaperBondGeometry {...chainGeometry} viewportZoom={viewport.zoom} />
        ) : (
          chainPreviewLines.map((preview) => (
            <line key={preview.id} x1={preview.start.x} y1={preview.start.y} x2={preview.end.x} y2={preview.end.y} />
          ))
        )}
      </g>
      <g className="selection-markers">
        {renderedLines
          .filter((line) => selection.bondIds.includes(line.id))
          .map((line) => (
            <line
              key={line.id}
              className="selected-bond"
              x1={roundToOneDecimal(line.start.x * paperSize.width)}
              y1={roundToOneDecimal(line.start.y * paperSize.height)}
              x2={roundToOneDecimal(line.end.x * paperSize.width)}
              y2={roundToOneDecimal(line.end.y * paperSize.height)}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        {svgAtoms
          .filter((atom) => selection.atomIds.includes(atom.id))
          .map((atom) => (
            <circle
              key={atom.id}
              className="selected-atom"
              cx={atom.x}
              cy={atom.y}
              r={10 / viewport.zoom}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        {hasSelection(selection)
          ? (() => {
              const points = renderedAtoms
                .filter((atom) => selection.atomIds.includes(atom.id))
                .map((atom) => normalizedToPixel(atom.position, paperSize));
              if (points.length < 2) return null;
              const padding = 14 / viewport.zoom;
              return (
                <rect
                  className="selection-boundary"
                  x={Math.min(...points.map((p) => p.x)) - padding}
                  y={Math.min(...points.map((p) => p.y)) - padding}
                  width={Math.max(...points.map((p) => p.x)) - Math.min(...points.map((p) => p.x)) + 2 * padding}
                  height={Math.max(...points.map((p) => p.y)) - Math.min(...points.map((p) => p.y)) + 2 * padding}
                />
              );
            })()
          : null}
        {marqueePreview ? (
          <rect
            className="selection-marquee"
            x={marqueePreview.x}
            y={marqueePreview.y}
            width={marqueePreview.width}
            height={marqueePreview.height}
          />
        ) : null}
        {lassoPreview ? (
          <polygon
            className="selection-lasso"
            points={lassoPreview.map((point) => `${point.x},${point.y}`).join(' ')}
          />
        ) : null}
      </g>
      <g className="validation-markers">
        {renderedLines
          .filter((line) => warningBondIds.has(line.id) && !invalidBondIds.has(line.id))
          .map((line) => (
            <line
              key={`warning-${line.id}`}
              className="warning-bond"
              x1={roundToOneDecimal(line.start.x * paperSize.width)}
              y1={roundToOneDecimal(line.start.y * paperSize.height)}
              x2={roundToOneDecimal(line.end.x * paperSize.width)}
              y2={roundToOneDecimal(line.end.y * paperSize.height)}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        {svgAtoms
          .filter((atom) => warningAtomIds.has(atom.id) && !invalidAtomIds.has(atom.id))
          .map((atom) => (
            <circle
              key={`warning-${atom.id}`}
              className="warning-atom"
              cx={atom.x}
              cy={atom.y}
              r={12 / viewport.zoom}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        {renderedLines
          .filter((line) => invalidBondIds.has(line.id))
          .map((line) => (
            <line
              key={line.id}
              className="invalid-bond"
              x1={roundToOneDecimal(line.start.x * paperSize.width)}
              y1={roundToOneDecimal(line.start.y * paperSize.height)}
              x2={roundToOneDecimal(line.end.x * paperSize.width)}
              y2={roundToOneDecimal(line.end.y * paperSize.height)}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        {svgAtoms
          .filter((atom) => invalidAtomIds.has(atom.id))
          .map((atom) => (
            <circle
              key={atom.id}
              className="invalid-atom"
              cx={atom.x}
              cy={atom.y}
              r={12 / viewport.zoom}
              vectorEffect="non-scaling-stroke"
            />
          ))}
      </g>
      <g className="atom-labels">
        {svgAtoms.filter(atomShowsLabel).map((atom) => (
          // A label is part of the depiction and scales with it: the parent <svg>'s viewBox
          // already grows every user unit with viewport zoom, so the label follows the bonds on
          // zoom-in the same way wedges do (#385). The only extra factor is labelScale, which
          // shrinks labels in step with a large molecule whose bonds were scaled down to fit the
          // canvas -- see usePaperGeometry -- so they stay proportional rather than swamping the
          // structure. It is not counter-scaled against zoom.
          <g
            key={atom.id}
            data-atom-id={atom.id}
            // #868 -- the label's own colour (or its element colour) via the variable its text uses.
            style={(() => {
              const color = atomLabelColor(atomAppearance?.[atom.id], atom.atomicNumber, undefined);
              return color ? ({ '--line-color': color } as CSSProperties) : undefined;
            })()}
            transform={
              labelScale === 1
                ? undefined
                : `translate(${atom.x} ${atom.y}) scale(${labelScale}) translate(${-atom.x} ${-atom.y})`
            }
          >
            <text className="atom-symbol" x={atom.x} y={atom.y}>
              {atom.symbol}
            </text>
            {atom.hydrogenX !== undefined && atom.hydrogenY !== undefined ? (
              <text className="implicit-hydrogen" x={atom.hydrogenX} y={atom.hydrogenY}>
                {implicitHydrogenLabel(atom.implicitHydrogenCount)}
              </text>
            ) : null}
            {atom.chargeLabel ? (
              <text className="formal-charge-label" x={atom.chargeX} y={atom.chargeY}>
                {atom.chargeLabel}
              </text>
            ) : null}
            {atom.radicalLabel ? (
              <text className="radical-label" x={atom.x - atom.chargeOffsetX} y={atom.y - 9}>
                {atom.radicalLabel}
              </text>
            ) : null}
          </g>
        ))}
      </g>
      {showNodeNumbers ? (
        <g className="node-number-labels">
          {/* Outlined badges distinguish node indices from chemical superscripts/subscripts. */}
          {svgAtoms.map((atom) => {
            const badge = nodeNumberBadgeBox(String(atom.nodeNumber), labelScale);
            return (
              <g key={atom.id}>
                <title>{`Node ${atom.nodeNumber} (${atom.id})`}</title>
                <rect
                  className="node-number-badge"
                  x={atom.nodeNumberX + badge.maskX}
                  y={atom.nodeNumberY + badge.maskY}
                  width={badge.maskWidth}
                  height={badge.maskHeight}
                  rx={3 * labelScale}
                  style={{ strokeWidth: labelScale }}
                  pointerEvents="none"
                />
                <text
                  className="node-number-label"
                  data-node-id={atom.id}
                  x={atom.nodeNumberX}
                  y={atom.nodeNumberY}
                  style={{ fontSize: 11 * labelScale }}
                  pointerEvents="visiblePainted"
                >
                  {atom.nodeNumber}
                </text>
              </g>
            );
          })}
        </g>
      ) : null}
      {showStereoLabels ? (
        <g className="stereo-labels">
          {/* #1047 -- each stereocentre's configuration, as perceived from the drawing. */}
          {svgAtoms.flatMap((atom) =>
            atom.stereoLabel && atom.stereoLabelX !== undefined && atom.stereoLabelY !== undefined
              ? [
                  <text
                    key={atom.id}
                    className="stereo-label"
                    data-testid="stereo-label"
                    data-node-id={atom.id}
                    x={atom.stereoLabelX}
                    y={atom.stereoLabelY}
                    style={{ fontSize: 12 * labelScale }}
                    pointerEvents="none"
                  >
                    {atom.stereoLabel}
                  </text>
                ]
              : []
          )}
          {bondStereoLabels.map((label) => (
            <text
              key={label.id}
              className="stereo-label"
              data-testid="stereo-label"
              data-bond-id={label.id}
              x={label.x}
              y={label.y}
              style={{ fontSize: 12 * labelScale }}
              pointerEvents="none"
            >
              {label.text}
            </text>
          ))}
        </g>
      ) : null}
      {showBondAcceptors ? (
        <g className="bond-acceptor-markers">
          {/* #576: hasOpenValence (drawingDocumentProjections.ts) -- the atom's current bond-order
              sum is below its maximum supported valence, covering every element that table has an
              entry for (including carbon, unlike implicitHydrogenCount). */}
          {svgAtoms
            .filter((atom) => atom.hasOpenValence)
            .map((atom) => (
              <circle
                key={atom.id}
                className="bond-acceptor-marker"
                cx={atom.x}
                cy={atom.y}
                r={12 / viewport.zoom}
                vectorEffect="non-scaling-stroke"
              />
            ))}
        </g>
      ) : null}
      {rotationPivotMarker ? (
        <g
          className="rotation-pivot-marker"
          transform={`translate(${rotationPivotMarker.x}, ${rotationPivotMarker.y})`}
        >
          <circle r={9 / viewport.zoom} vectorEffect="non-scaling-stroke" />
          <line x1={-15 / viewport.zoom} y1={0} x2={15 / viewport.zoom} y2={0} vectorEffect="non-scaling-stroke" />
          <line x1={0} y1={-15 / viewport.zoom} x2={0} y2={15 / viewport.zoom} vectorEffect="non-scaling-stroke" />
        </g>
      ) : null}
      <g className="draft-lines active-geometry-preview">
        {draftGeometry ? (
          <PaperBondGeometry {...draftGeometry} viewportZoom={viewport.zoom} />
        ) : (
          svgDrafts.map((line) => (
            <line
              key={line.pointerId}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              vectorEffect="non-scaling-stroke"
            />
          ))
        )}
      </g>
      <g className="proposal-markers">
        {proposalMarkers.map((marker) => (
          <circle
            key={marker.key}
            className={marker.state}
            cx={roundToOneDecimal(marker.x)}
            cy={roundToOneDecimal(marker.y)}
            r={proposalRadiusPx / viewport.zoom}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </g>
      {hoveredEndpoint ? (
        <circle
          className="existing-endpoint-marker"
          cx={roundToOneDecimal(hoveredEndpoint.x)}
          cy={roundToOneDecimal(hoveredEndpoint.y)}
          r={proposalRadiusPx / viewport.zoom}
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
      <g className="existing-endpoint-markers">
        {offeredExistingEndpoints.map((marker) => (
          <circle
            key={marker.pointerId}
            className="existing-endpoint-marker"
            cx={roundToOneDecimal(marker.x)}
            cy={roundToOneDecimal(marker.y)}
            r={proposalRadiusPx / viewport.zoom}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </g>
      <g className="blink-markers">
        {blinkMarkers.map((marker) => (
          <circle
            key={marker.id}
            cx={roundToOneDecimal(marker.x)}
            cy={roundToOneDecimal(marker.y)}
            r={proposalRadiusPx / viewport.zoom}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </g>
      <g className="annotation-layer" style={{ strokeWidth: annotationStrokeWidth }}>
        {annotations
          .filter((annotation) => !isBackgroundShape(annotation))
          .map((annotation) => {
            if (annotation.kind === 'text') {
              return (
                <PaperTextAnnotation
                  key={annotation.id}
                  annotation={annotation}
                  paperSize={paperSize}
                  selected={selection.annotationIds?.includes(annotation.id) ?? false}
                />
              );
            }
            const stroke = annotation.color ?? 'currentColor';
            const dash = ANNOTATION_DASH[annotation.lineStyle ?? 'solid'];
            // #859 -- an authored width (pt) is drawn at its real size: points to drawing units,
            // times the zoom, as the on-screen width the shared geometry and non-scaling strokes use.
            const strokeWidth =
              annotation.lineWidth !== undefined ? pointsToUnits(annotation.lineWidth) * viewport.zoom : undefined;
            const ownWidth = strokeWidth !== undefined ? { strokeWidth } : undefined;
            if (annotation.kind === 'shape') {
              return (
                <PaperShapeAnnotation
                  key={annotation.id}
                  annotation={annotation}
                  paperSize={paperSize}
                  viewport={viewport}
                />
              );
            }

            if (annotation.kind === 'reaction-arrow') {
              const start = normalizedToPixel(annotation.from, paperSize);
              const end = normalizedToPixel(annotation.to, paperSize);
              const primitives = reactionArrowPrimitives(
                start,
                end,
                annotation.arrowKind,
                annotation.overlay,
                strokeWidth ?? annotationStrokeWidth,
                viewport.zoom
              );
              return (
                <g
                  key={annotation.id}
                  data-annotation-id={annotation.id}
                  data-arrow-kind={annotation.arrowKind}
                  data-overlay={annotation.overlay}
                  style={ownWidth}
                  opacity={annotation.opacity}
                >
                  {primitives.map((primitive, index) => {
                    if (primitive.t === 'polygon') {
                      return (
                        <polygon
                          key={index}
                          points={primitive.points.map((point) => `${point.x},${point.y}`).join(' ')}
                          fill={primitive.filled ? stroke : 'none'}
                          stroke={primitive.head ? 'none' : stroke}
                          vectorEffect="non-scaling-stroke"
                        />
                      );
                    }
                    if (primitive.t === 'path') {
                      return (
                        <path
                          key={index}
                          d={primitive.d}
                          fill="none"
                          stroke={stroke}
                          strokeLinecap="butt"
                          strokeDasharray={primitive.dashed ? (dash ?? '6 4') : dash}
                          vectorEffect="non-scaling-stroke"
                        />
                      );
                    }
                    return (
                      <polyline
                        key={index}
                        points={primitive.points.map((point) => `${point.x},${point.y}`).join(' ')}
                        fill="none"
                        stroke={stroke}
                        strokeLinecap="butt"
                        strokeDasharray={primitive.dashed ? (dash ?? '6 4') : dash}
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })}
                </g>
              );
            }

            const from = normalizedToPixel(annotation.from, paperSize);
            const to = normalizedToPixel(annotation.to, paperSize);
            return (
              <g
                key={annotation.id}
                data-annotation-id={annotation.id}
                data-head={annotation.head}
                style={ownWidth}
                opacity={annotation.opacity}
              >
                {curvedArrowPrimitives(
                  from,
                  to,
                  annotation.head,
                  annotation.bow,
                  strokeWidth ?? annotationStrokeWidth,
                  viewport.zoom,
                  annotation.controls?.map((point) => normalizedToPixel(point, paperSize)) as
                    [PixelPoint, PixelPoint] | undefined,
                  atomEndGaps(annotation.fromAnchor, annotation.toAnchor)
                ).map((primitive, index) => {
                  if (primitive.t === 'path') {
                    return (
                      <path
                        key={index}
                        d={primitive.d}
                        fill="none"
                        stroke={stroke}
                        strokeLinecap="butt"
                        strokeDasharray={dash}
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  }
                  if (primitive.t === 'polygon') {
                    return <polygon key={index} points={pointsAttr(primitive.points)} fill={stroke} stroke="none" />;
                  }
                  return (
                    <polyline
                      key={index}
                      points={pointsAttr(primitive.points)}
                      fill="none"
                      stroke={stroke}
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })}
              </g>
            );
          })}
      </g>
      <g className="placement-preview">
        {shapePreview ? (
          <rect
            className="shape-placement-preview"
            x={shapePreview.minX}
            y={shapePreview.minY}
            width={shapePreview.maxX - shapePreview.minX}
            height={shapePreview.maxY - shapePreview.minY}
            fill="none"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </g>
      <g className="path-control-lines" stroke="#2563eb" strokeWidth="1" strokeDasharray="3 3" pointerEvents="none">
        {objectHandles
          .filter((h) => h.anchor)
          .map((h) => {
            const a = normalizedToPixel(h.anchor!, paperSize),
              b = normalizedToPixel(h.point, paperSize);
            return <line key={h.id} x1={a.x} y1={a.y} x2={b.x} y2={b.y} vectorEffect="non-scaling-stroke" />;
          })}
      </g>
      <g className="object-handles" stroke="#2563eb" fill="white" strokeWidth="1.5">
        {objectHandles.map((handle) => {
          const p = normalizedToPixel(handle.point, paperSize);
          // #838 -- for a molecule, the per-atom endpoint handles and the centre move handle stay
          // pressable but aren't drawn (they made a "sea of points"): the pointer shows what a press
          // grabs instead, and the whole selection box moves. Rotate and scale stay visible.
          const quiet =
            handle.id.startsWith('atom-') ||
            (handle.id === 'move' &&
              objectHandles.some((other) => other.id.startsWith('atom-') || other.id === 'pointer-tip'));
          return (
            <circle
              key={handle.id}
              className={quiet ? 'object-handle object-handle--quiet' : 'object-handle'}
              data-object-handle={handle.id}
              data-selected={handle.selected || undefined}
              fill={handle.selected ? '#2563eb' : 'white'}
              cx={p.x}
              cy={p.y}
              r={(handle.id === 'move' ? 6 : 5) / viewport.zoom}
              vectorEffect="non-scaling-stroke"
            >
              <title>
                {handle.id === 'pointer-tip'
                  ? 'Drag pointer tip onto an atom, bond, cycle center or shape to attach; empty space detaches'
                  : handle.id.startsWith('atom-')
                    ? 'Move bond endpoint'
                    : handle.id.startsWith('control-')
                      ? 'Adjust path curve'
                      : handle.id.startsWith('point-')
                        ? 'Reshape path'
                        : `${handle.id} selected object`}
              </title>
            </circle>
          );
        })}
      </g>
      <g className="arrow-snap-targets" pointerEvents="none" data-testid="arrow-snap-targets">
        {arrowSnapTargets.map((point, index) => {
          const pixel = normalizedToPixel(point, paperSize);
          return (
            <circle
              key={index}
              data-arrow-snap-target=""
              cx={pixel.x}
              cy={pixel.y}
              r={11 / viewport.zoom}
              fill="rgba(37, 99, 235, 0.16)"
              stroke="#2563eb"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </g>
      <g className="callout-snap-targets" pointerEvents="none" data-testid="callout-snap-targets">
        {calloutTargets.map((target) => {
          const p = normalizedToPixel(target.point, paperSize);
          const active = target.id === activeCalloutTarget;
          return (
            <circle
              key={target.id}
              data-callout-target={target.id}
              data-target-kind={target.kind}
              data-active={active || undefined}
              cx={p.x}
              cy={p.y}
              r={(active ? 5 : 3) / viewport.zoom}
              fill="none"
              stroke="var(--accent, #2563eb)"
              strokeOpacity={active ? 1 : 0.65}
              strokeWidth={active ? 1.75 : 1}
              vectorEffect="non-scaling-stroke"
            >
              <title>
                {target.kind === 'cycle'
                  ? 'Cycle center'
                  : target.kind === 'bond'
                    ? 'Bond midpoint'
                    : target.kind === 'atom'
                      ? 'Atom'
                      : 'Shape center'}
              </title>
            </circle>
          );
        })}
      </g>
      <g className="arrow-handles" stroke="#2563eb" fill="white" strokeWidth="1.5">
        {annotations.flatMap((annotation) => {
          if (
            annotation.kind === 'shape' ||
            annotation.kind === 'text' ||
            !selection.annotationIds?.includes(annotation.id)
          )
            return [];
          const handles = arrowHandlePoints(annotation, paperSize);
          return [
            ...(annotation.kind === 'curved-arrow'
              ? [0, 1].map((index) => {
                  const endpoint = normalizedToPixel(index === 0 ? annotation.from : annotation.to, paperSize);
                  const control = normalizedToPixel(handles[index + 2]!.point, paperSize);
                  return (
                    <line
                      key={`${annotation.id}-guide-${index}`}
                      x1={endpoint.x}
                      y1={endpoint.y}
                      x2={control.x}
                      y2={control.y}
                      strokeDasharray="4 4"
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })
              : []),
            ...handles.map(({ handle, point }) => {
              const pixel = normalizedToPixel(point, paperSize);
              return (
                <circle
                  key={`${annotation.id}-${handle}`}
                  data-arrow-handle={handle}
                  data-arrow-id={annotation.id}
                  // #933: an end anchored to an atom or bond is a filled handle; a free one is hollow.
                  {...((handle === 'from' && annotation.fromAnchor && annotation.fromAnchor.kind !== 'point') ||
                  (handle === 'to' && annotation.toAnchor && annotation.toAnchor.kind !== 'point')
                    ? { fill: '#2563eb', 'data-anchored': '' }
                    : {})}
                  cx={pixel.x}
                  cy={pixel.y}
                  r={5 / viewport.zoom}
                  vectorEffect="non-scaling-stroke"
                />
              );
            })
          ];
        })}
      </g>
    </svg>
  );
}
